/**
 * Authentication Context
 *
 * Provides centralized authentication state management including:
 * - User login/logout functionality
 * - JWT token management
 * - Authentication status tracking
 * - Integration with backend API
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { authService } from "../components/auth/authService";
import {
  setAccessTokenCookie,
  setUserDataCookie,
  clearAccessTokenCookie,
  clearUserDataCookie,
} from "./auth/cookieHelpers";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [token, setToken] = useState(null);

  // Initialize auth state from localStorage on mount
  useEffect(() => {
    const initializeAuth = () => {
      try {
        const savedToken = localStorage.getItem("token");
        const savedUser = localStorage.getItem("user_data");

        if (savedToken && savedUser) {
          const parsedUser = JSON.parse(savedUser);

          setToken(savedToken);
          setUser(parsedUser);
          setIsAuthenticated(true);

          // IMPORTANT: Sync to cookies for sub-website access
          // This ensures cookies are set even after page refresh
          setAccessTokenCookie(savedToken);
          setUserDataCookie(parsedUser);
        }
      } catch (error) {
        // Clear invalid data
        localStorage.removeItem("token");
        localStorage.removeItem("user_data");
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  // Login function
  const login = async (email, password) => {
    setIsLoading(true);

    try {
      const response = await authService.login(email, password);

      if (!response.success) {
        console.error("Login failed:", response.error);
        throw new Error(response.error || "Login failed");
      }

      // Extract user and token from response
      const { access_token, user: userData } = response.data;

      // Save to localStorage
      localStorage.setItem("token", access_token);
      localStorage.setItem("user_data", JSON.stringify(userData));

      // Also set cookie so backend payment endpoint can read it
      setAccessTokenCookie(access_token);
      setUserDataCookie(userData);

      // Update state
      setToken(access_token);
      setUser(userData);
      setIsAuthenticated(true);

      return { success: true, data: response.data };
    } catch (error) {
      console.error("Login error:", error);
      return {
        success: false,
        error: error.message || "Login failed. Please try again.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Google login function
  const loginWithGoogle = async (googleToken) => {
    setIsLoading(true);
    try {
      const response = await authService.loginWithGoogle(googleToken);

      if (!response.success) {
        console.error("Google login failed:", response.error);
        return {
          success: false,
          error: response.error || "Google login failed. Please try again.",
        };
      }

      // Extract user data and token from response
      const { access_token, user } = response.data;

      if (!access_token || !user) {
        console.error(
          "Invalid Google login response - missing token or user data",
        );
        return {
          success: false,
          error: "Invalid response from server. Please try again.",
        };
      }

      // Store authentication data
      localStorage.setItem("token", access_token);
      localStorage.setItem("user_data", JSON.stringify(user));

      // Also set cookie so backend payment endpoint can read it
      setAccessTokenCookie(access_token);
      setUserDataCookie(user);

      setToken(access_token);
      setUser(user);
      setIsAuthenticated(true);

      return { success: true, data: response.data };
    } catch (error) {
      console.error("Google login error:", error);
      return {
        success: false,
        error: error.message || "Google login failed. Please try again.",
      };
    } finally {
      setIsLoading(false);
    }
  };

  // Logout function
  const logout = useCallback(async () => {
    setIsLoading(true);
    try {
      // Call backend logout endpoint if token exists
      if (token) {
        await authService.logout(token).catch((error) => {
          // Don't fail logout if backend call fails
          console.warn("Backend logout failed:", error);
        });
      }
    } catch (error) {
      console.warn("Logout API call failed:", error);
    } finally {
      // Always clear local state regardless of API call result
      localStorage.removeItem("token");
      localStorage.removeItem("user_data");

      setToken(null);
      setUser(null);
      setIsAuthenticated(false);
      setIsLoading(false);

      // Clear cookie expected by backend
      clearAccessTokenCookie();
      clearUserDataCookie();
    }
  }, [token]);

  // Get authenticated API headers
  const getAuthHeaders = () => {
    return {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    };
  };

  // Check if token is expired (basic check - in production use proper JWT parsing)
  const isTokenExpired = useCallback(() => {
    if (!token) return true;

    try {
      // Basic token expiry check - in production decode JWT properly
      const tokenParts = token.split(".");
      if (tokenParts.length !== 3) return true;

      const payload = JSON.parse(atob(tokenParts[1]));
      const currentTime = Date.now() / 1000;

      return payload.exp < currentTime;
    } catch (error) {
      console.error("Error checking token expiry:", error);
      return true;
    }
  }, [token]);

  // Auto logout when token expires
  useEffect(() => {
    if (isAuthenticated && token && isTokenExpired()) {
      logout();
    }
  }, [isAuthenticated, token, logout, isTokenExpired]);

  const value = {
    // State
    user,
    isAuthenticated,
    isLoading,
    token,

    // Actions
    login,
    loginWithGoogle,
    logout,

    // Utilities
    getAuthHeaders,
    isTokenExpired,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
